import { useId } from "react";
import { motion, type Transition } from "motion/react";
import type { IconProps } from "../icon";
import { Icon } from "../icon";
import { cn } from "../cn";

const springTransition: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 22,
};

const bounceTransition: Transition = {
  type: "spring",
  stiffness: 400,
  damping: 15,
};

// ============================================================================
// 1. ANIMATED MENU (Hamburger ↔ X)
// ============================================================================

export interface AnimatedMenuIconProps extends IconProps {
  isOpen?: boolean;
}

export function AnimatedMenuIcon({ isOpen = false, className, ...props }: AnimatedMenuIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.g animate={{ rotate: isOpen ? 90 : 0 }} transition={springTransition} style={{ transformOrigin: "12px 12px" }}>
        <motion.line
          x1="4"
          y1="6"
          x2="20"
          y2="6"
          animate={isOpen ? { y1: 12, y2: 12, rotate: 45 } : { y1: 6, y2: 6, rotate: 0 }}
          transition={springTransition}
          style={{ transformOrigin: "12px 12px" }}
        />
        <motion.line
          x1="4"
          y1="12"
          x2="20"
          y2="12"
          animate={{ opacity: isOpen ? 0 : 1, scaleX: isOpen ? 0 : 1 }}
          transition={{ duration: 0.15 }}
          style={{ transformOrigin: "12px 12px" }}
        />
        <motion.line
          x1="4"
          y1="18"
          x2="20"
          y2="18"
          animate={isOpen ? { y1: 12, y2: 12, rotate: -45 } : { y1: 18, y2: 18, rotate: 0 }}
          transition={springTransition}
          style={{ transformOrigin: "12px 12px" }}
        />
      </motion.g>
    </Icon>
  );
}

// ============================================================================
// 2. ANIMATED CHECK (Validation en ligne continue)
// ============================================================================

export interface AnimatedCheckIconProps extends IconProps {
  checked?: boolean;
}

export function AnimatedCheckIcon({ checked = false, className, ...props }: AnimatedCheckIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.circle
        cx="12"
        cy="12"
        r="9"
        animate={{
          scale: checked ? [1, 1.1, 1] : 1,
          opacity: checked ? 1 : 0.3,
        }}
        transition={springTransition}
        style={{ transformOrigin: "12px 12px" }}
      />
      <motion.path
        d="M 7 12 L 10.5 15.5 L 17 8.5"
        initial={false}
        animate={{
          pathLength: checked ? 1 : 0,
          opacity: checked ? 1 : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 350,
          damping: 25,
          opacity: { duration: 0.1 },
        }}
      />
    </Icon>
  );
}

// ============================================================================
// 3. ANIMATED THEME (Soleil ↔ Crescent Moon parfait)
// ============================================================================

export interface AnimatedThemeIconProps extends IconProps {
  theme?: "light" | "dark";
}

export function AnimatedThemeIcon({ theme = "light", className, ...props }: AnimatedThemeIconProps) {
  const isDark = theme === "dark";
  const maskId = useId();

  return (
    <Icon
      className={cn(
        "transition-colors duration-300",
        isDark ? "text-violet-400 dark:text-violet-300" : "text-amber-500",
        className
      )}
      style={{ overflow: "visible" }}
      {...props}
    >
      <motion.g
        animate={{ rotate: isDark ? 40 : 0 }}
        transition={springTransition}
        style={{ transformOrigin: "12px 12px" }}
      >
        <mask id={maskId}>
          <rect x="0" y="0" width="24" height="24" fill="white" />
          <motion.circle
            initial={false}
            animate={{
              cx: isDark ? 17 : 26,
              cy: isDark ? 7 : -4,
              r: isDark ? 7 : 0,
            }}
            transition={springTransition}
            fill="black"
          />
        </mask>

        <motion.circle
          cx="12"
          cy="12"
          initial={false}
          animate={{ r: isDark ? 8.5 : 4 }}
          transition={springTransition}
          mask={`url(#${maskId})`}
          fill="currentColor"
          stroke="none"
        />

        <motion.g
          initial={false}
          animate={{
            opacity: isDark ? 0 : 1,
            scale: isDark ? 0.3 : 1,
            rotate: isDark ? 90 : 0,
          }}
          transition={springTransition}
          style={{ transformOrigin: "12px 12px" }}
        >
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </motion.g>
      </motion.g>
    </Icon>
  );
}

// ============================================================================
// 4. ANIMATED COPY (Document ↔ Coche sans saut de layout)
// ============================================================================

export interface AnimatedCopyIconProps extends IconProps {
  copied?: boolean;
}

export function AnimatedCopyIcon({ copied = false, className, ...props }: AnimatedCopyIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.g
        animate={{
          opacity: copied ? 0 : 1,
          scale: copied ? 0.5 : 1,
          rotate: copied ? -15 : 0,
        }}
        transition={springTransition}
        style={{ transformOrigin: "12px 12px" }}
      >
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </motion.g>

      <motion.g
        animate={{
          opacity: copied ? 1 : 0,
          scale: copied ? 1 : 0.4,
        }}
        transition={bounceTransition}
        style={{ transformOrigin: "12px 12px" }}
      >
        <motion.path
          d="M20 6L9 17l-5-5"
          className="text-emerald-500"
          strokeWidth="2.25"
          initial={false}
          animate={{ pathLength: copied ? 1 : 0 }}
          transition={springTransition}
        />
        <motion.circle
          cx="12"
          cy="4"
          r="1"
          fill="currentColor"
          animate={{ y: copied ? -3 : 0, opacity: copied ? [0, 1, 0] : 0 }}
          transition={{ duration: 0.45 }}
        />
        <motion.circle
          cx="20"
          cy="12"
          r="1"
          fill="currentColor"
          animate={{ x: copied ? 3 : 0, opacity: copied ? [0, 1, 0] : 0 }}
          transition={{ duration: 0.45 }}
        />
        <motion.circle
          cx="4"
          cy="12"
          r="1"
          fill="currentColor"
          animate={{ x: copied ? -3 : 0, opacity: copied ? [0, 1, 0] : 0 }}
          transition={{ duration: 0.45 }}
        />
      </motion.g>
    </Icon>
  );
}

// ============================================================================
// 5. ANIMATED EYE (Masquage / Occultation de l'œil)
// ============================================================================

export interface AnimatedEyeIconProps extends IconProps {
  visible?: boolean;
}

export function AnimatedEyeIcon({ visible = true, className, ...props }: AnimatedEyeIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <motion.circle
        cx="12"
        cy="12"
        r="2.75"
        animate={{
          scale: visible ? 1 : 0.6,
          opacity: visible ? 1 : 0.3,
        }}
        transition={springTransition}
        style={{ transformOrigin: "12px 12px" }}
      />
      <motion.line
        x1="3"
        y1="3"
        x2="21"
        y2="21"
        initial={false}
        animate={{
          pathLength: visible ? 0 : 1,
          opacity: visible ? 0 : 1,
        }}
        transition={springTransition}
      />
    </Icon>
  );
}

// ============================================================================
// 6. ANIMATED HEART (Cœur avec Transition de Remplissage & Onde Élastique)
// ============================================================================

export interface AnimatedHeartIconProps extends IconProps {
  active?: boolean;
}

export function AnimatedHeartIcon({ active = false, className, ...props }: AnimatedHeartIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.circle
        cx="12"
        cy="12"
        r="8"
        initial={false}
        animate={active ? { scale: [0.8, 1.6], opacity: [0.6, 0] } : { scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ transformOrigin: "12px 12px" }}
        fill="currentColor"
      />
      <motion.path
        d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
        fill="currentColor"
        initial={false}
        animate={{
          scale: active ? [1, 1.28, 0.92, 1.1, 1] : 1,
          fillOpacity: active ? 1 : 0,
        }}
        transition={{
          scale: bounceTransition,
          fillOpacity: { duration: 0.25, ease: "easeInOut" },
        }}
        style={{ transformOrigin: "12px 12px" }}
      />
    </Icon>
  );
}

// ============================================================================
// 7. ANIMATED STAR (Étoile avec Transition de Remplissage & Rotation 72°)
// ============================================================================

export interface AnimatedStarIconProps extends IconProps {
  active?: boolean;
}

export function AnimatedStarIcon({ active = false, className, ...props }: AnimatedStarIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.polygon
        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
        fill="currentColor"
        initial={false}
        animate={{
          rotate: active ? [0, 72] : 0,
          scale: active ? [1, 1.25, 1] : 1,
          fillOpacity: active ? 1 : 0,
        }}
        transition={{
          rotate: bounceTransition,
          scale: bounceTransition,
          fillOpacity: { duration: 0.2, ease: "easeInOut" },
        }}
        style={{ transformOrigin: "12px 12px" }}
      />
    </Icon>
  );
}

// ============================================================================
// 8. ANIMATED LOCK (Refonte SOTA du Cadenas avec Serrure & Anse Pivotante)
// ============================================================================

export interface AnimatedLockIconProps extends IconProps {
  unlocked?: boolean;
}

export function AnimatedLockIcon({ unlocked = false, className, ...props }: AnimatedLockIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      {/* Corps du cadenas avec serrure intégrée */}
      <rect x="4" y="11" width="16" height="11" rx="2.5" />
      
      {/* Serrure lumineuse (trou de serrure) */}
      <motion.g
        animate={{
          scale: unlocked ? [1, 1.3, 1] : 1,
        }}
        transition={springTransition}
        style={{ transformOrigin: "12px 16px" }}
      >
        <circle cx="12" cy="15.5" r="1.25" fill="currentColor" />
        <line x1="12" y1="16.5" x2="12" y2="18.5" strokeWidth="2" strokeLinecap="round" />
      </motion.g>

      {/* Anse pivotante fluide */}
      <motion.path
        d="M7 11V7a5 5 0 0 1 10 0v4"
        initial={false}
        animate={
          unlocked
            ? { y: -3.5, rotate: -42 }
            : { y: 0, rotate: 0 }
        }
        transition={{
          type: "spring",
          stiffness: 380,
          damping: 18,
        }}
        style={{ transformOrigin: "7px 11px" }}
      />
    </Icon>
  );
}

// ============================================================================
// 9. ANIMATED CHEVRON (Rotation 180°)
// ============================================================================

export interface AnimatedChevronIconProps extends IconProps {
  expanded?: boolean;
}

export function AnimatedChevronIcon({ expanded = false, className, ...props }: AnimatedChevronIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.g
        animate={{ rotate: expanded ? 180 : 0 }}
        transition={springTransition}
        style={{ transformOrigin: "12px 12px" }}
      >
        <path d="M6 9l6 6 6-6" />
      </motion.g>
    </Icon>
  );
}

// ============================================================================
// 10. ANIMATED BELL (Cloche de Notification avec Oscillation & Badge)
// ============================================================================

export interface AnimatedBellIconProps extends IconProps {
  ringing?: boolean;
}

export function AnimatedBellIcon({ ringing = false, className, ...props }: AnimatedBellIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.g
        animate={
          ringing
            ? { rotate: [0, -18, 18, -12, 12, -6, 0] }
            : { rotate: 0 }
        }
        transition={{ duration: 0.6, ease: "easeInOut" }}
        style={{ transformOrigin: "12px 4px" }}
      >
        {/* Corps de la cloche */}
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        {/* Battant */}
        <motion.path
          d="M13.73 21a2 2 0 0 1-3.46 0"
          animate={ringing ? { x: [-1.5, 1.5, -1, 1, 0] } : { x: 0 }}
          transition={{ duration: 0.6 }}
        />
      </motion.g>

      {/* Pastille de notification rouge/accent */}
      <motion.circle
        cx="18"
        cy="5"
        r="2.5"
        className="text-rose-500"
        fill="currentColor"
        initial={false}
        animate={{
          scale: ringing ? [0, 1.3, 1] : 0,
          opacity: ringing ? 1 : 0,
        }}
        transition={bounceTransition}
        style={{ transformOrigin: "18px 5px" }}
      />
    </Icon>
  );
}

// ============================================================================
// 11. ANIMATED VOLUME (Haut-Parleur ↔ Muted avec Vagues Audio)
// ============================================================================

export interface AnimatedVolumeIconProps extends IconProps {
  muted?: boolean;
}

export function AnimatedVolumeIcon({ muted = false, className, ...props }: AnimatedVolumeIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      {/* Haut-parleur */}
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />

      {/* Vague sonore 1 */}
      <motion.path
        d="M15.5 8.5a5 5 0 0 1 0 7"
        initial={false}
        animate={{
          pathLength: muted ? 0 : 1,
          opacity: muted ? 0 : 1,
        }}
        transition={springTransition}
      />

      {/* Vague sonore 2 */}
      <motion.path
        d="M19 5a10 10 0 0 1 0 14"
        initial={false}
        animate={{
          pathLength: muted ? 0 : 1,
          opacity: muted ? 0 : 1,
        }}
        transition={{ ...springTransition, delay: 0.05 }}
      />

      {/* Barre oblique de coupure de son */}
      <motion.line
        x1="22"
        y1="6"
        x2="16"
        y2="18"
        className="text-rose-500"
        strokeWidth="2"
        initial={false}
        animate={{
          pathLength: muted ? 1 : 0,
          opacity: muted ? 1 : 0,
        }}
        transition={springTransition}
      />
    </Icon>
  );
}

// ============================================================================
// 12. ANIMATED UPLOAD (Flèche d'Envoi / Launch)
// ============================================================================

export interface AnimatedUploadIconProps extends IconProps {
  uploading?: boolean;
}

export function AnimatedUploadIcon({ uploading = false, className, ...props }: AnimatedUploadIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      {/* Plateau de réception */}
      <motion.path
        d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"
        animate={{ scaleY: uploading ? [1, 0.9, 1] : 1 }}
        transition={springTransition}
        style={{ transformOrigin: "12px 18px" }}
      />

      {/* Flèche d'envoi volante */}
      <motion.g
        animate={
          uploading
            ? { y: [-12, 0], opacity: [0, 1] }
            : { y: 0, opacity: 1 }
        }
        transition={bounceTransition}
      >
        <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" />
      </motion.g>
    </Icon>
  );
}

// ============================================================================
// 13. ANIMATED TRASH (Poubelle avec Ouverture de Couvercle)
// ============================================================================

export interface AnimatedTrashIconProps extends IconProps {
  open?: boolean;
}

export function AnimatedTrashIcon({ open = false, className, ...props }: AnimatedTrashIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      {/* Bac à ordures */}
      <path d="M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13M10 11v6M14 11v6" />

      {/* Couvercle pivotant */}
      <motion.g
        animate={
          open
            ? { y: -3.5, rotate: -25 }
            : { y: 0, rotate: 0 }
        }
        transition={springTransition}
        style={{ transformOrigin: "4px 7px" }}
      >
        <path d="M4 7h16M9 7V4h6v3" />
      </motion.g>
    </Icon>
  );
}

// ============================================================================
// 14. ANIMATED BOOKMARK (Marque-Page avec Enfoncement)
// ============================================================================

export interface AnimatedBookmarkIconProps extends IconProps {
  active?: boolean;
}

export function AnimatedBookmarkIcon({ active = false, className, ...props }: AnimatedBookmarkIconProps) {
  return (
    <Icon className={className} style={{ overflow: "visible" }} {...props}>
      <motion.path
        d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
        fill="currentColor"
        initial={false}
        animate={{
          y: active ? [0, 3, 0] : 0,
          scale: active ? [1, 1.1, 1] : 1,
          fillOpacity: active ? 1 : 0,
        }}
        transition={bounceTransition}
        style={{ transformOrigin: "12px 12px" }}
      />
    </Icon>
  );
}
